import type { IEventEmitter } from "./types";

/**
 * Generic EventEmitter class for type-safe event handling
 * Supports multiple listeners per event
 * No constraint on Events to support strict interfaces
 */
export class EventEmitter<Events = any> implements IEventEmitter<Events> {
  private _listeners: Map<keyof Events, Set<Function>> = new Map();

  /**
   * Add an event listener
   * @param event - The event name
   * @param handler - The event handler function
   */
  on<K extends keyof Events>(event: K, handler: Events[K]): void {
    if (!this._listeners.has(event)) {
      this._listeners.set(event, new Set());
    }
    this._listeners.get(event)!.add(handler as Function);
  }

  /**
   * Add an event listener that only fires once
   * @param event - The event name
   * @param handler - The event handler function
   */
  once<K extends keyof Events>(event: K, handler: Events[K]): void {
    const onceWrapper = ((...args: any[]) => {
      this.off(event, onceWrapper as Events[K]);
      (handler as Function)(...args);
    }) as Events[K];

    this.on(event, onceWrapper);
  }

  /**
   * Remove an event listener
   * @param event - The event name
   * @param handler - The specific handler to remove (optional - removes all if not provided)
   */
  off<K extends keyof Events>(event: K, handler?: Events[K]): void {
    if (!this._listeners.has(event)) return;

    if (handler) {
      // Remove specific handler
      this._listeners.get(event)!.delete(handler as Function);
    } else {
      // Remove all handlers for this event
      this._listeners.delete(event);
    }
  }

  /**
   * Emit an event to all registered listeners
   * @param event - The event name
   * @param args - Arguments to pass to the event handlers
   */
  public emit<K extends keyof Events>(
    event: K,
    ...args: Events[K] extends (...args: infer P) => any ? P : never[]
  ): void {
    const handlers = this._listeners.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        (handler as any)(...args);
      });
    }
  }

  /**
   * Remove all listeners for a specific event or all events
   * @param event - The event name (optional - removes all if not provided)
   */
  removeAllListeners<K extends keyof Events>(event?: K): void {
    if (event) {
      this._listeners.delete(event);
    } else {
      this._listeners.clear();
    }
  }

  /**
   * Get the number of listeners for a specific event
   * @param event - The event name
   * @returns The number of listeners
   */
  listenerCount<K extends keyof Events>(event: K): number {
    return this._listeners.get(event)?.size || 0;
  }

  /**
   * Get all registered event names
   * @returns Array of event names that have listeners
   */
  eventNames(): Array<keyof Events> {
    return Array.from(this._listeners.keys());
  }

  /**
   * Get all listeners for a specific event
   * @param event - The event name
   * @returns Array of listener functions
   */
  listeners<K extends keyof Events>(event: K): Array<Events[K]> {
    const handlers = this._listeners.get(event);
    return handlers ? (Array.from(handlers) as Array<Events[K]>) : [];
  }
}
